import { createRoute } from '@tanstack/react-router';
import { lazy } from 'react';
import { z } from 'zod';
import { appLayoutRoute, publicLayoutRoute } from '../../router/parents';

// Pages load on demand (React.lazy; the router renders each match inside Suspense).
const CompaniesPage = lazy(() => import('./pages/companies').then((m) => ({ default: m.CompaniesPage })));
const CompanyPage = lazy(() => import('./pages/companies').then((m) => ({ default: m.CompanyPage })));
const ContactPage = lazy(() => import('./pages/contacts').then((m) => ({ default: m.ContactPage })));
const ContactsPage = lazy(() => import('./pages/contacts').then((m) => ({ default: m.ContactsPage })));
const DuplicatesPage = lazy(() => import('./pages/data').then((m) => ({ default: m.DuplicatesPage })));
const ImportPage = lazy(() => import('./pages/data').then((m) => ({ default: m.ImportPage })));
const EstimatePage = lazy(() => import('./pages/estimates').then((m) => ({ default: m.EstimatePage })));
const EstimatesPage = lazy(() => import('./pages/estimates').then((m) => ({ default: m.EstimatesPage })));
const DealPage = lazy(() => import('./pages/pipeline').then((m) => ({ default: m.DealPage })));
const PipelinePage = lazy(() => import('./pages/pipeline').then((m) => ({ default: m.PipelinePage })));
const PipelineSettingsPage = lazy(() =>
  import('./pages/pipeline').then((m) => ({ default: m.PipelineSettingsPage })),
);
const ProposalPage = lazy(() => import('./pages/proposals').then((m) => ({ default: m.ProposalPage })));
const ProposalsPage = lazy(() => import('./pages/proposals').then((m) => ({ default: m.ProposalsPage })));
const PublicProposalPage = lazy(() =>
  import('./pages/proposals').then((m) => ({ default: m.PublicProposalPage })),
);
const TakeoffPage = lazy(() => import('./pages/takeoff').then((m) => ({ default: m.TakeoffPage })));
const ReportsPage = lazy(() => import('./pages/reports').then((m) => ({ default: m.ReportsPage })));
const MyDayPage = lazy(() => import('./pages/tasks').then((m) => ({ default: m.MyDayPage })));
const TasksPage = lazy(() => import('./pages/tasks').then((m) => ({ default: m.TasksPage })));
const TemplatesPage = lazy(() => import('./pages/templates').then((m) => ({ default: m.TemplatesPage })));
const VendorPage = lazy(() => import('./pages/vendors').then((m) => ({ default: m.VendorPage })));
const VendorsPage = lazy(() => import('./pages/vendors').then((m) => ({ default: m.VendorsPage })));

/**
 * CRM routes. `/crm` (the sidebar link) is "My day". Every route is exported in `crmRoutes`.
 * The public proposal-signing page (`/sign/proposal/$token`) is exported separately as
 * `crmPublicRoutes` and mounted under the public layout (no sign-in).
 */
const entitySearch = z.object({ entity: z.enum(['contact', 'company']).catch('contact').default('contact') });

const crmIndexRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/crm',
  component: MyDayPage,
});
const tasksRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/crm/tasks',
  component: TasksPage,
});

const pipelineRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/crm/pipeline',
  validateSearch: z.object({
    pipelineId: z.string().optional(),
    view: z.enum(['board', 'table']).catch('board').optional(),
  }),
  component: function PipelineRoute() {
    const search = pipelineRoute.useSearch();
    return <PipelinePage pipelineId={search.pipelineId} view={search.view ?? 'board'} />;
  },
});
const pipelineSettingsRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/crm/pipeline/settings',
  component: PipelineSettingsPage,
});
const dealRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/crm/deals/$dealId',
  /** `?convert=true` opens "Convert to project" (offered after a deal is won elsewhere). */
  validateSearch: z.object({ convert: z.boolean().optional().catch(undefined) }),
  component: function DealRoute() {
    const { dealId } = dealRoute.useParams();
    const { convert } = dealRoute.useSearch();
    return <DealPage key={dealId} dealId={dealId} convert={convert} />;
  },
});

const contactsRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/crm/contacts',
  component: ContactsPage,
});
const contactRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/crm/contacts/$contactId',
  component: function ContactRoute() {
    const { contactId } = contactRoute.useParams();
    return <ContactPage key={contactId} contactId={contactId} />;
  },
});
const companiesRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/crm/companies',
  component: CompaniesPage,
});
const companyRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/crm/companies/$companyId',
  component: function CompanyRoute() {
    const { companyId } = companyRoute.useParams();
    return <CompanyPage key={companyId} companyId={companyId} />;
  },
});
const duplicatesRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/crm/duplicates',
  validateSearch: entitySearch,
  component: function DuplicatesRoute() {
    const { entity } = duplicatesRoute.useSearch();
    return <DuplicatesPage key={entity} entity={entity} />;
  },
});
const importRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/crm/import',
  validateSearch: entitySearch,
  component: function ImportRoute() {
    const { entity } = importRoute.useSearch();
    return <ImportPage key={entity} entity={entity} />;
  },
});

const vendorsRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/crm/vendors',
  component: VendorsPage,
});
const vendorRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/crm/vendors/$companyId',
  component: function VendorRoute() {
    const { companyId } = vendorRoute.useParams();
    return <VendorPage key={companyId} companyId={companyId} />;
  },
});

const estimatesRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/crm/estimates',
  component: EstimatesPage,
});
const estimateRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/crm/estimates/$estimateId',
  component: function EstimateRoute() {
    const { estimateId } = estimateRoute.useParams();
    return <EstimatePage key={estimateId} estimateId={estimateId} />;
  },
});
/** On-plan takeoff of one estimate (plan sheets, measurements, send to estimate). */
const takeoffRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/crm/estimates/$estimateId/takeoff',
  component: function TakeoffRoute() {
    const { estimateId } = takeoffRoute.useParams();
    return <TakeoffPage key={estimateId} estimateId={estimateId} />;
  },
});
const proposalsRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/crm/proposals',
  component: ProposalsPage,
});
const proposalRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/crm/proposals/$proposalId',
  component: function ProposalRoute() {
    const { proposalId } = proposalRoute.useParams();
    return <ProposalPage key={proposalId} proposalId={proposalId} />;
  },
});

const reportsRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/crm/reports',
  component: ReportsPage,
});
const templatesRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/crm/templates',
  component: TemplatesPage,
});

/** Public signing page for clients (token link from the proposal email). */
const signProposalRoute = createRoute({
  getParentRoute: () => publicLayoutRoute,
  path: '/sign/proposal/$token',
  component: function SignProposalRoute() {
    const { token } = signProposalRoute.useParams();
    return <PublicProposalPage token={token} />;
  },
});

export const crmRoutes = [
  crmIndexRoute,
  tasksRoute,
  pipelineRoute,
  pipelineSettingsRoute,
  dealRoute,
  contactsRoute,
  contactRoute,
  companiesRoute,
  companyRoute,
  duplicatesRoute,
  importRoute,
  vendorsRoute,
  vendorRoute,
  estimatesRoute,
  estimateRoute,
  takeoffRoute,
  proposalsRoute,
  proposalRoute,
  reportsRoute,
  templatesRoute,
] as const;

/** Routes under the public layout (no sign-in). */
export const crmPublicRoutes = [signProposalRoute] as const;
